import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import LayoutContext from 'components/Layout/LayoutContext';
import PageTitle from 'components/__SHARED__/PageTitle/PageTitle';
import { ITopBarMenuItem } from 'components/__SHARED__/TopBar/TopBarMenuItem';

import { StatusMensagens } from 'helpers/enums/AssembleiaStatus';
import { TopBarMenuAcessoRapido } from 'helpers/enums/TopBarMenuAcessoRapido';

import { assessorActionAlteraStatusForum } from 'store/modules/assessor/actions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';

import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { getAcessoRapido } from 'helpers/utils/TopBarMenuUtils';

import IncluirMensagenModal from './IncluirMensagemModal';
import useStyle from './style';

import { AssessorRelatoriosRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import MensagensContext from 'pages/Assessor/MensagensAssessor/Context';
import { AuthUserPerfilId } from 'store/modules/auth/IAuthUserPerfil';

interface PathParams {
  idEvento: string;
}

export default function Header() {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { evento, atualizarEvento } = useContext(MensagensContext);
  const { idEvento } = useParams<PathParams>();
  const layoutContext = useContext(LayoutContext);
  const { loadingDuvidas, loadingEvento } = useSelector(
    (state: IReducer) => state.assembleiasAssessor
  );
  const { statusForumIsChanging } = useSelector(
    (state: IReducer) => state.assessor
  );
  const { usuario } = useSelector((state: IReducer) => state.auth);

  const eventoNome = useMemo(() => {
    const nome = evento ? evento.nome : '';
    return loadingEvento ? translation.MensagensAssessorRoute.carregando : nome;
  }, [loadingEvento, evento]);

  const statusForum = useCallback(
    async (status: StatusMensagens) => {
      if (!evento) return;
      dispatch(
        assessorActionAlteraStatusForum(
          evento.id,
          status,
          ErrorHandler,
          atualizarEvento
        )
      );
    },
    [dispatch, evento, atualizarEvento]
  );

  const incluirMensagem = useCallback(() => {
    if (!evento) return;
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        IncluirMensagenModal,
        {
          genericProps: {
            idEvento: evento.id,
            key: modalKey,
          },
        },
        modalKey
      )
    );
  }, [dispatch, evento]);

  // const baixarRelatorioChat = useCallback(async () => {
  //   if (!evento) return;
  //   const { fail, data } = await relatoriosApi.duvidas(evento.id);

  //   if (fail) {
  //     ErrorHandler(fail);
  //   }
  //   if (data) {
  //     const fileDate = getFormatedDateTime(Date.now(), DateFormats.ISOFile);
  //     DownloadTrigger(
  //       `Relatorio-Mensagens_${fileDate}_Evento-${evento.id}.xls`,
  //       data
  //     );
  //   }
  // }, [evento]);

  const statusChat = useMemo(() => {
    return evento?.statusChat ? evento.statusChat : StatusMensagens.Bloqueado;
  }, [evento]);

  const subMenu = useMemo<ITopBarMenuItem[]>(() => {
    if (!usuario) return [];
    const menus: ITopBarMenuItem[] = getAcessoRapido(
      translation,
      usuario!.perfil,
      TopBarMenuAcessoRapido.Mensagens,
      idEvento,
      evento?.tipo
    );

    menus.push({
      label: translation.SubMenus.relatorio,
      onClick: () =>
        window.open(
          AssessorRelatoriosRoutePath.replace(':id', idEvento.toString()),
          '_blank'
        ),
      disabled: [AuthUserPerfilId.PertencerChat].includes(
        usuario!?.perfil!?.id!
      ),
    });

    menus.push({
      label:
        statusChat === StatusMensagens.Bloqueado
          ? translation.SubMenus.habilitarChat
          : translation.SubMenus.desabilitarChat,
      disabled:
        statusForumIsChanging ||
        loadingDuvidas ||
        loadingEvento ||
        (usuario.perfil.id === 'pertencer_consulta' &&
          !usuario.perfil.assessorRoute.duvidasSugestoes.alterarSituacao &&
          evento?.tipo !== 'REU') ||
        (usuario.perfil.id !== 'pertencer_consulta' &&
          !usuario.perfil.assessorRoute.duvidasSugestoes.alterarSituacao),
      onClick: () => {
        statusForum(
          statusChat === StatusMensagens.Bloqueado
            ? StatusMensagens.LiberadoConvocados
            : StatusMensagens.Bloqueado
        );
      },
    });

    menus.push({
      label: translation.SubMenus.incluirMensagem,
      disabled:
        statusForumIsChanging ||
        loadingDuvidas ||
        loadingEvento ||
        (usuario.perfil.id === 'pertencer_consulta' &&
          !usuario.perfil.assessorRoute.duvidasSugestoes.incluirMensagem &&
          evento?.tipo !== 'REU') ||
        (usuario.perfil.id !== 'pertencer_consulta' &&
          !usuario.perfil.assessorRoute.duvidasSugestoes.incluirMensagem),
      onClick: () => incluirMensagem(),
    });

    return menus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    idEvento,
    usuario,
    statusForumIsChanging,
    loadingDuvidas,
    loadingEvento,
    statusChat,
  ]);

  useEffect(() => {
    layoutContext.setPageMenuItems(subMenu);
    return () => {
      layoutContext.setPageMenuItems();
    };
    // eslint-disable-next-line
  }, [subMenu]);

  return (
    <header className={classes.root}>
      <PageTitle title={eventoNome} />
    </header>
  );
}
